import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  BellRing,
  ChevronDown,
  ChevronRight,
  Clock3,
  Delete,
  Headphones,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOff,
  Play,
  Search,
  Ticket,
  UserRound,
  X,
} from 'lucide-react';
import { sipManager } from '../SipManager';
import { AudioDevicePanel } from './AudioDevicePanel';
import { telephonyApi } from '../telephonyApi';
import {
  createBrowserIncomingCallAlerts,
  type IncomingCallAlertStatus,
} from '../IncomingCallAlerts';
import { useOperatorStatus } from '../hooks/useOperatorStatus';
import { useSipPhone } from '../hooks/useSipPhone';
import {
  filterTransferTargets,
  transferTargetInitials,
  transferTargetStatusLabel,
  transferTargetSubtitle,
  transferTargetTitle,
} from '../TransferTargets';
import { inboundTicketSelection } from '../InboundTicketSelection';
import type { PhoneContact, PhoneState, TransferTarget } from '../types';
import { compactCallPartyPresentation } from '../../client-history/presentation';

const STATE_LABELS: Record<PhoneState, string> = {
  OFFLINE: 'Телефон отключён',
  PASSIVE: 'Телефон временно пассивен',
  CONNECTING: 'Подключение к АТС',
  READY: 'Готов принимать звонки',
  RINGING: 'Входящий звонок',
  DIALING: 'Исходящий звонок',
  ACTIVE: 'Разговор',
  HOLD: 'Звонок на удержании',
  ERROR: 'Ошибка телефона',
};

function durationLabel(
  startedAt: number | null,
  endedAt: number | null | undefined,
  now: number,
): string {
  if (!startedAt) return '00:00';

  const effectiveNow = endedAt || now;
  const seconds = Math.max(
    0,
    Math.floor((effectiveNow - startedAt) / 1000),
  );
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function displayNumber(call: ReturnType<typeof useSipPhone>['call']): string {
  if (!call) return '';
  return call.direction === 'INBOUND'
    ? String(call.from_number || call.to_number || '')
    : String(call.to_number || call.from_number || '');
}


function contactPhoneOptions(contact: PhoneContact): string[] {
  const channelPhones = (contact.channels || [])
    .filter(channel => channel.channel_type === 'PHONE' && channel.is_active !== false)
    .sort((left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)))
    .map(channel => String(channel.value || '').trim())
    .filter(Boolean);
  const direct = String(contact.phone || '').trim();
  return [...new Set([direct, ...channelPhones].filter(Boolean))];
}

export function CallCard() {
  const phone = useSipPhone();
  const operator = useOperatorStatus();
  const navigate = useNavigate();
  const effectiveOperatorStatus = phone.snapshot.registered ? operator.status : 'OFFLINE';
  const effectiveOperatorLabel = phone.snapshot.registered ? operator.label : 'Офлайн';
  const [open, setOpen] = useState(false);
  const [incomingAlertPending, setIncomingAlertPending] = useState(false);
  const [incomingAlertMessage, setIncomingAlertMessage] = useState('');
  const [incomingAlertStatus, setIncomingAlertStatus] = useState<IncomingCallAlertStatus>(() => ({
    soundReady: false,
    notificationPermission: typeof window !== 'undefined' && 'Notification' in window
      ? window.Notification.permission
      : 'unsupported',
  }));
  const [dialNumber, setDialNumber] = useState(() => {
    try {
      return sessionStorage.getItem('intez:browser-phone:dial-number') || '';
    } catch {
      return '';
    }
  });
  const [selectedContact, setSelectedContact] = useState<PhoneContact | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSearch, setTransferSearch] = useState('');
  const [selectedTransferTargetId, setSelectedTransferTargetId] = useState('');
  const [selectingTicketId, setSelectingTicketId] = useState('');
  const [dialpadOpen, setDialpadOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const dockRef = useRef<HTMLButtonElement | null>(null);
  const incomingAlertsRef = useRef<ReturnType<typeof createBrowserIncomingCallAlerts> | null>(null);
  if (!incomingAlertsRef.current && typeof window !== 'undefined') {
    incomingAlertsRef.current = createBrowserIncomingCallAlerts(() => setOpen(true));
  }
  const contactsQuery = useQuery({
    queryKey: ['browser-phone-contacts'],
    queryFn: telephonyApi.contacts,
    enabled: open && phone.snapshot.registered,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const transferTargetsQuery = useQuery({
    queryKey: ['telephony-transfer-targets'],
    queryFn: telephonyApi.transferTargets,
    enabled: transferOpen && ['ACTIVE', 'HOLD'].includes(phone.snapshot.state),
    staleTime: 5_000,
    refetchInterval: transferOpen ? 5_000 : false,
    retry: 1,
  });
  const transferTargets = useMemo(
    () => filterTransferTargets(transferTargetsQuery.data || [], transferSearch),
    [transferSearch, transferTargetsQuery.data],
  );
  const selectedTransferTarget = (transferTargetsQuery.data || [])
    .find(target => target.targetId === selectedTransferTargetId);
  const transferGroupTargets = transferTargets.filter(target => target.type === 'GROUP');
  const transferEmployeeTargets = transferTargets.filter(target => target.type === 'USER');
  const contactMatches = useMemo(() => {
    const query = dialNumber.trim().toLocaleLowerCase('ru-RU');
    if (query.length < 2 || selectedContact) return [];
    return (contactsQuery.data || [])
      .filter(contact => contact.is_active !== false && contactPhoneOptions(contact).length > 0)
      .filter(contact => {
        const haystack = [
          contact.full_name,
          contact.company_name,
          ...contactPhoneOptions(contact),
        ].join(' ').toLocaleLowerCase('ru-RU');
        return haystack.includes(query);
      })
      .slice(0, 6);
  }, [contactsQuery.data, dialNumber, selectedContact]);
  const number = phone.snapshot.state === 'RINGING'
    ? phone.snapshot.remoteNumber || displayNumber(phone.call)
    : displayNumber(phone.call) || phone.snapshot.remoteNumber;
  const ticketSelection = useMemo(
    () => inboundTicketSelection(phone.call, phone.snapshot.state),
    [phone.call, phone.snapshot.state],
  );

  useEffect(() => {
    sipManager.bindAudioElement(audioRef.current);
    return () => sipManager.bindAudioElement(null);
  }, []);

  useEffect(() => {
    const alerts = incomingAlertsRef.current;
    if (!alerts) return;
    let active = true;

    const removeUnlockListeners = () => {
      window.removeEventListener('pointerdown', unlockFromGesture, true);
      window.removeEventListener('keydown', unlockFromGesture, true);
    };
    const unlockFromGesture = () => {
      removeUnlockListeners();
      void alerts.unlockSound().then(status => {
        if (active) setIncomingAlertStatus(status);
      });
    };

    window.addEventListener('pointerdown', unlockFromGesture, true);
    window.addEventListener('keydown', unlockFromGesture, true);

    return () => {
      active = false;
      removeUnlockListeners();
      alerts.stop();
    };
  }, []);

  useEffect(() => {
    const alerts = incomingAlertsRef.current;
    if (!alerts) return;
    const incomingRinging = phone.snapshot.state === 'RINGING'
      && phone.snapshot.sessionDirection === 'INBOUND';

    if (!incomingRinging) {
      alerts.stop();
      return;
    }

    alerts.start(number);
    const syncPageAttention = () => alerts.syncPageAttention();
    document.addEventListener('visibilitychange', syncPageAttention);
    window.addEventListener('blur', syncPageAttention);
    window.addEventListener('focus', syncPageAttention);

    return () => {
      document.removeEventListener('visibilitychange', syncPageAttention);
      window.removeEventListener('blur', syncPageAttention);
      window.removeEventListener('focus', syncPageAttention);
      alerts.stop();
    };
  }, [number, phone.snapshot.sessionDirection, phone.snapshot.state]);

  useEffect(() => {
    if (['RINGING', 'DIALING', 'ACTIVE', 'HOLD'].includes(phone.snapshot.state)) {
      setOpen(true);
    }
    if (!['ACTIVE', 'HOLD'].includes(phone.snapshot.state)) {
      setTransferOpen(false);
      setTransferSearch('');
      setSelectedTransferTargetId('');
    }
  }, [phone.snapshot.state]);

  // BEGIN INTEZ PHONE WIDGET STABLE V14.3
  useEffect(() => {
    if (!open) return;

    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (cardRef.current?.contains(target)) return;
      if (dockRef.current?.contains(target)) return;
      setOpen(false);
    };

    const closeByEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', closeOutside, true);
    document.addEventListener('keydown', closeByEscape);

    return () => {
      document.removeEventListener('pointerdown', closeOutside, true);
      document.removeEventListener('keydown', closeByEscape);
    };
  }, [open]);
  // END INTEZ PHONE WIDGET STABLE V14.3

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem('intez:browser-phone:dial-number', dialNumber);
    } catch {
      // sessionStorage может быть недоступен в защищённом режиме браузера.
    }
  }, [dialNumber]);

  const duration = durationLabel(
    phone.snapshot.startedAt,
    phone.snapshot.endedAt,
    now,
  );
  const callParty = compactCallPartyPresentation(phone.call);
  const title = phone.call ? callParty.title : phone.config?.account?.displayName || 'Встроенный телефон';
  const showCard = open;
  const showIncomingAlertSetup = !incomingAlertStatus.soundReady
    || incomingAlertStatus.notificationPermission !== 'granted';

  const prepareIncomingAlerts = async () => {
    const alerts = incomingAlertsRef.current;
    if (!alerts || incomingAlertPending) return;
    setIncomingAlertPending(true);
    setIncomingAlertMessage('');
    try {
      const status = await alerts.prepare();
      setIncomingAlertStatus(status);
      if (status.notificationPermission === 'default') {
        setIncomingAlertMessage('Разрешение на системные уведомления пока не выдано.');
      } else if (status.notificationPermission === 'denied') {
        setIncomingAlertMessage('Системные уведомления заблокированы в настройках сайта.');
      } else if (!status.soundReady) {
        setIncomingAlertMessage('Браузер не разрешил воспроизведение звука.');
      }
    } finally {
      setIncomingAlertPending(false);
    }
  };

  const startCall = async (event: FormEvent) => {
    event.preventDefault();
    await phone.dial({
      phone: dialNumber,
      ...(selectedContact?.id ? { contactId: selectedContact.id } : {}),
      ...(selectedContact?.company_id ? { companyId: selectedContact.company_id } : {}),
      ...(selectedContact?.full_name ? { title: selectedContact.full_name } : {}),
    });
  };

  const submitTransfer = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedTransferTarget) return;
    await phone.transfer(selectedTransferTarget.destination);
    setTransferSearch('');
    setSelectedTransferTargetId('');
    setTransferOpen(false);
  };

  const openTicket = () => {
    const id = phone.call?.ticket_id;
    if (!id) return;
    setOpen(false);
    navigate(`/tickets/${id}`);
  };

  const chooseTicket = async (ticketId: string) => {
    if (!ticketSelection.enabled || selectingTicketId) return;
    setSelectingTicketId(ticketId);
    try {
      await phone.selectTicket(ticketId);
    } catch {
      // Текст ошибки уже показан общим состоянием телефона.
    } finally {
      setSelectingTicketId('');
    }
  };

  const renderTransferTarget = (target: TransferTarget) => <button
    type="button"
    key={target.targetId}
    className={`${selectedTransferTargetId === target.targetId ? 'active' : ''} ${target.type === 'GROUP' ? 'group' : ''}`.trim()}
    onClick={() => setSelectedTransferTargetId(target.targetId)}
    role="option"
    aria-selected={selectedTransferTargetId === target.targetId}
  >
    <i>{transferTargetInitials(target)}</i>
    <span>
      <b>{transferTargetTitle(target)}</b>
      <small>{transferTargetSubtitle(target)}</small>
    </span>
    {target.type === 'GROUP'
      ? <em className="status-group"><i />Группа</em>
      : <em className={`status-${target.operatorStatus.toLowerCase()}`}>
        <i />{transferTargetStatusLabel(target.operatorStatus)}
      </em>}
  </button>;

  return <>
    <audio ref={audioRef} autoPlay playsInline className="browser-phone-audio" />
    <button
      type="button"
      ref={dockRef}
      data-phone-visual="focus"
      className={`browser-phone-dock operator-${effectiveOperatorStatus.toLowerCase()} ${phone.snapshot.registered ? 'ready' : phone.snapshot.state === 'ERROR' ? 'error' : phone.snapshot.state === 'CONNECTING' ? 'connecting' : ''} ${phone.call ? 'has-call' : ''}`}
      onClick={() => setOpen(value => !value)}
      aria-expanded={showCard}
      title={`${STATE_LABELS[phone.snapshot.state]}${phone.statusMessage ? ` · ${phone.statusMessage}` : ''}`}
    >
      {phone.call?.direction === 'INBOUND'
        ? <PhoneIncoming />
        : phone.call
          ? <PhoneCall />
          : phone.snapshot.registered || phone.snapshot.state === 'CONNECTING'
            ? <Headphones />
            : <PhoneOff />}
      <span className="browser-phone-dock-copy">
        <b>{phone.call ? title : phone.snapshot.registered ? `доб. ${phone.config?.account?.extension || '—'}` : phone.snapshot.state === 'CONNECTING' ? 'Подключение…' : 'Телефон'}</b>
        <small>{phone.call ? STATE_LABELS[phone.snapshot.state] : phone.snapshot.registered ? effectiveOperatorLabel : STATE_LABELS[phone.snapshot.state]}</small>
      </span>
      {phone.call && <time>{duration}</time>}
      <i />
    </button>

    {showCard && <aside ref={cardRef} data-phone-visual="air" className={`browser-phone-card state-${phone.snapshot.state.toLowerCase()}`} aria-live="assertive">
      <header className="browser-phone-card-head">
        <span className="browser-phone-direction">
          {phone.call?.direction === 'INBOUND' ? <ArrowDownLeft /> : phone.call?.direction === 'OUTBOUND' ? <ArrowUpRight /> : <Headphones />}
        </span>
        <div>
          <small>{STATE_LABELS[phone.snapshot.state]}</small>
          <strong>{title}</strong>
          {phone.call && callParty.matched && <em>{callParty.number}</em>}
        </div>
        <span className="browser-phone-timer"><Clock3 /> {duration}</span>
        <button type="button" className="icon-button" onClick={() => setOpen(false)} title="Свернуть"><ChevronDown /></button>
      </header>

      {phone.call && <>
        <div className="browser-phone-client">
          <div className="browser-phone-client-avatar"><UserRound /></div>
          <div>
            <b>{callParty.company}</b>
            <span>{phone.call.ticket_display_number
              ? `${phone.call.ticket_display_number} · ${phone.call.ticket_subject || 'Заявка'}`
              : ticketSelection.visible
                ? `Найдено активных заявок: ${ticketSelection.candidates.length}`
                : 'Определяем заявку для звонка…'}</span>
          </div>
        </div>
        {ticketSelection.visible && <section className="browser-phone-ticket-selection">
          <header className="browser-phone-ticket-selection-head">
            <span><Ticket /></span>
            <div>
              <small>Связь со звонком</small>
              <b>Выберите заявку</b>
              <p>{ticketSelection.hint || 'Запись разговора появится в истории выбранной заявки.'}</p>
            </div>
            <em>{ticketSelection.candidates.length}</em>
          </header>
          <div className="browser-phone-ticket-list" role="listbox" aria-label="Активные заявки клиента">
            {ticketSelection.candidates.map(candidate => <button
              type="button"
              key={candidate.id}
              role="option"
              aria-selected={selectingTicketId === candidate.id}
              disabled={!ticketSelection.enabled || Boolean(selectingTicketId)}
              onClick={() => void chooseTicket(candidate.id)}
            >
              <span className="browser-phone-ticket-number">{candidate.displayNumber}</span>
              <span className="browser-phone-ticket-copy">
                <b>{candidate.subject || 'Без темы'}</b>
                <small>{candidate.statusLabel} · обновлена {candidate.updatedLabel}</small>
              </span>
              {selectingTicketId === candidate.id
                ? <em className="browser-phone-ticket-pending">Связываем…</em>
                : <ChevronRight />}
            </button>)}
          </div>
        </section>}
        {phone.call.ticket_id && <button
          type="button"
          className="button primary"
          onClick={openTicket}
        >
          Открыть заявку {phone.call.ticket_display_number || ''}
        </button>}
      </>}

      {phone.statusMessage && <div className="browser-phone-message">{phone.statusMessage}</div>}

      {!phone.call && phone.config?.account && phone.snapshot.state !== 'PASSIVE' && <section className="browser-phone-operator-control">
        <div>
          <small>Статус оператора</small>
          <b>{effectiveOperatorLabel}</b>
        </div>
        <label>
          <span className={`browser-phone-status-dot status-${effectiveOperatorStatus.toLowerCase()}`} />
          <select
            value={effectiveOperatorStatus}
            onChange={event => void operator.setStatus(event.target.value as typeof operator.status)}
            disabled={operator.pending || phone.actionPending || !phone.snapshot.primaryTab || !phone.snapshot.registered}
            aria-label="Статус оператора телефонии"
          >
            {operator.options
              .filter(item => !['CALL', 'POST_PROCESSING'].includes(item.value))
              .map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
      </section>}

      {!phone.call && phone.snapshot.state === 'PASSIVE' && <section className="browser-phone-passive">
        <Headphones />
        <div>
          <b>Телефон временно пассивен</b>
          <p>Портал автоматически проверяет и снимает зависшую блокировку. Для немедленного подключения нажмите кнопку ниже.</p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => void phone.activateThisTab()}
          disabled={phone.actionPending}
        >
          <Headphones /> {phone.actionPending ? 'Активация…' : 'Подключить телефон здесь'}
        </button>
      </section>}

      {!phone.call && ['READY', 'OFFLINE', 'CONNECTING', 'ERROR'].includes(phone.snapshot.state) && <form className="browser-phone-dial-form" onSubmit={startCall}>
        {showIncomingAlertSetup && <section className="browser-phone-alert-setup">
          <BellRing />
          <div>
            <b>Оповещения о входящих звонках</b>
            <small>{incomingAlertStatus.notificationPermission === 'denied'
              ? 'Звук работает после активации. Системные уведомления заблокированы — разрешите их в настройках сайта.'
              : incomingAlertStatus.notificationPermission === 'unsupported'
                ? 'Этот браузер не поддерживает системные уведомления. Можно включить звук звонка.'
                : 'Включите звук и системное уведомление, чтобы не пропустить звонок в другой вкладке.'}</small>
            {incomingAlertMessage && <em>{incomingAlertMessage}</em>}
          </div>
          {(incomingAlertStatus.notificationPermission === 'default' || !incomingAlertStatus.soundReady) && <button
            type="button"
            className="button secondary"
            onClick={() => void prepareIncomingAlerts()}
            disabled={incomingAlertPending}
          >
            <BellRing /> {incomingAlertPending ? 'Включаем…' : incomingAlertStatus.notificationPermission === 'default' ? 'Включить' : 'Включить звук'}
          </button>}
        </section>}
        <AudioDevicePanel disabled={phone.actionPending || !phone.snapshot.primaryTab} />
        <label>Номер телефона</label>
        <div className="browser-phone-inline-form">
          <input
            value={dialNumber}
            onChange={event => { setDialNumber(event.target.value); setSelectedContact(null); }}
            placeholder="+7 999 000-00-00 или добавочный"
            inputMode="tel"
            autoComplete="tel"
            enterKeyHint="go"
            maxLength={80}
            aria-label="Номер телефона для звонка"
          />
          <button className="button primary" disabled={!dialNumber.trim() || !phone.snapshot.registered || !phone.snapshot.primaryTab || phone.actionPending}>
            <Phone /> Позвонить
          </button>
        </div>
        {selectedContact && <div className="browser-phone-selected-contact"><UserRound /><span><b>{selectedContact.full_name || 'Контакт'}</b><small>{selectedContact.company_name || 'Без компании'}</small></span><button type="button" onClick={() => setSelectedContact(null)} aria-label="Сбросить контакт"><X /></button></div>}
        {Boolean(contactMatches.length) && <div className="browser-phone-contact-results">{contactMatches.map(contact => {
          const contactPhone = contactPhoneOptions(contact)[0];
          return <button type="button" key={contact.id} onClick={() => { setSelectedContact(contact); setDialNumber(contactPhone); }}><UserRound /><span><b>{contact.full_name || 'Без имени'}</b><small>{contact.company_name || 'Без компании'} · {contactPhone}</small></span></button>;
        })}</div>}
        {contactsQuery.isFetching && dialNumber.trim().length >= 2 && <small>Ищем контакт…</small>}
        {(!phone.snapshot.registered || !phone.snapshot.primaryTab) && phone.snapshot.state !== 'PASSIVE'
          && <small>Номер можно подготовить сейчас. Кнопка звонка включится после регистрации телефона.</small>}
      </form>}

      {phone.snapshot.state === 'RINGING' && <div className="browser-phone-answer-actions">
        <button type="button" className="button danger" onClick={() => void phone.decline()} disabled={phone.actionPending}><PhoneOff /> Отклонить</button>
        <button type="button" className="button success" onClick={() => {
          void phone.answer().catch(() => undefined);
        }} disabled={phone.actionPending}><PhoneIncoming /> {phone.call?.recovery_pending ? 'Восстановить' : 'Принять'}</button>
      </div>}

      {['DIALING', 'ACTIVE', 'HOLD'].includes(phone.snapshot.state) && <>
        <div className="browser-phone-controls">
          <button type="button" onClick={() => void phone.toggleMute()} disabled={phone.actionPending} title={phone.snapshot.muted ? 'Включить микрофон' : 'Выключить микрофон'}>
            {phone.snapshot.muted ? <MicOff /> : <Mic />}<span>{phone.snapshot.muted ? 'Включить' : 'Микрофон'}</span>
          </button>
          <button type="button" onClick={() => void phone.toggleHold()} disabled={phone.actionPending || phone.snapshot.state === 'DIALING'} title={phone.snapshot.held ? 'Вернуть с удержания' : 'Поставить на удержание'}>
            {phone.snapshot.held ? <Play /> : <Pause />}<span>{phone.snapshot.held ? 'Продолжить' : 'Удержание'}</span>
          </button>
          <button type="button" onClick={() => setDialpadOpen(value => !value)} title="DTMF-клавиатура"><Delete /><span>Клавиши</span></button>
          <button
            type="button"
            className={transferOpen ? 'active' : ''}
            onClick={() => {
              setTransferOpen(value => !value);
              setTransferSearch('');
              setSelectedTransferTargetId('');
            }}
            disabled={!phone.config?.account?.allowTransfer || phone.snapshot.state === 'DIALING'}
            title="Перевести звонок"
          ><PhoneForwarded /><span>Перевод</span></button>
        </div>

        {dialpadOpen && <div className="browser-phone-dialpad">
          {'123456789*0#'.split('').map(tone => <button type="button" key={tone} onClick={() => phone.sendDtmf(tone)}>{tone}</button>)}
        </div>}

        {transferOpen && <form className="browser-phone-transfer" onSubmit={submitTransfer}>
          <header className="browser-phone-transfer-head">
            <div>
              <small>Перевод звонка</small>
              <b>Выберите сотрудника или группу</b>
            </div>
            <span>{transferTargetsQuery.data?.length || 0}</span>
          </header>
          <label className="browser-phone-transfer-search">
            <Search />
            <input
              value={transferSearch}
              onChange={event => setTransferSearch(event.target.value)}
              placeholder="Имя, группа или добавочный"
              autoComplete="off"
              aria-label="Поиск сотрудника или группы для перевода"
            />
          </label>
          <div className="browser-phone-transfer-list" role="listbox" aria-label="Сотрудники и группы для перевода звонка">
            {transferGroupTargets.length > 0 && <div className="browser-phone-transfer-section">Группы маршрутизации</div>}
            {transferGroupTargets.map(renderTransferTarget)}
            {transferEmployeeTargets.length > 0 && <div className="browser-phone-transfer-section">Сотрудники</div>}
            {transferEmployeeTargets.map(renderTransferTarget)}
            {transferTargetsQuery.isLoading && <div className="browser-phone-transfer-state">Загружаем список…</div>}
            {transferTargetsQuery.isError && <div className="browser-phone-transfer-state error">Не удалось загрузить список. Попробуйте ещё раз.</div>}
            {!transferTargetsQuery.isLoading && !transferTargetsQuery.isError && transferTargets.length === 0 && <div className="browser-phone-transfer-state">{transferSearch.trim() ? 'Сотрудники и группы не найдены' : 'Нет доступных целей для перевода'}</div>}
          </div>
          <button className="button primary browser-phone-transfer-submit" disabled={!selectedTransferTarget || phone.actionPending}>
            <PhoneForwarded /> {phone.actionPending ? 'Переводим…' : selectedTransferTarget ? `Перевести · ${transferTargetTitle(selectedTransferTarget)}` : 'Выберите сотрудника или группу'}
          </button>
        </form>}

        <button type="button" className="browser-phone-hangup" onClick={() => void phone.hangup()} disabled={phone.actionPending}><PhoneOff /> Завершить звонок</button>
      </>}


    </aside>}
  </>;
}
